import { useState, useEffect } from 'react';
import { Modal, Button, Field, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { SERVICE_TYPES, CLEANING_METHODS, OCCASIONS } from '@/lib/constants';
import { todayISO } from '@/lib/utils';
import type { Closet, ServiceProvider } from '@/lib/types';

// ---------------------------------------------------------------------------
// Shared closet picker (used by several transition modals)
// ---------------------------------------------------------------------------

function ClosetSelect({
  value,
  onChange,
  closets,
}: {
  value: string;
  onChange: (id: string) => void;
  closets: Closet[];
}) {
  return (
    <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select closet…</option>
      {closets.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
          {c.isMain ? ' (Main)' : ''}
        </option>
      ))}
    </select>
  );
}

function useClosets() {
  const { data } = useApi<{ closets: Closet[] }>('/closets');
  return data?.closets ?? [];
}

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  onConfirm: (payload: Record<string, unknown>) => Promise<void> | void;
  busy?: boolean;
}

// ---------------------------------------------------------------------------
// Send for Service
// ---------------------------------------------------------------------------

export function SendForServiceModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const { data } = useApi<{ serviceProviders: ServiceProvider[] }>(isOpen ? '/service-providers' : null);
  const providers = data?.serviceProviders ?? [];
  const [providerId, setProviderId] = useState('');
  const [providerName, setProviderName] = useState('');
  const [serviceType, setServiceType] = useState<string>(SERVICE_TYPES[0]);
  const [dropOffDate, setDropOffDate] = useState(todayISO());
  const [expectedPickupDate, setExpectedPickupDate] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');

  const submit = () => {
    onConfirm({
      providerId: providerId || undefined,
      providerName: providerName || providers.find((p) => p.id === providerId)?.name || 'Service Provider',
      serviceType,
      dropOffDate: dropOffDate || undefined,
      expectedPickupDate: expectedPickupDate || undefined,
      cost: cost ? Number(cost) : undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send for Service"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth onClick={submit} disabled={busy || (!providerId && !providerName)}>
            Send
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Service Provider">
          <select
            className={inputClass}
            value={providerId}
            onChange={(e) => {
              setProviderId(e.target.value);
              const p = providers.find((x) => x.id === e.target.value);
              if (p) setProviderName(p.name);
            }}
          >
            <option value="">Select provider…</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {!providerId && (
          <Field label="Or enter provider name">
            <input
              className={inputClass}
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              placeholder="e.g., Jeeves of Belgravia"
            />
          </Field>
        )}
        <Field label="Service Type">
          <select className={inputClass} value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
            {SERVICE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Drop-off Date">
            <input type="date" className={inputClass} value={dropOffDate} onChange={(e) => setDropOffDate(e.target.value)} />
          </Field>
          <Field label="Expected Pickup">
            <input
              type="date"
              className={inputClass}
              value={expectedPickupDate}
              onChange={(e) => setExpectedPickupDate(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Estimated Cost">
          <input type="number" className={inputClass} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Notes">
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mark Returned (from service)
// ---------------------------------------------------------------------------

export function MarkReturnedModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const closets = useClosets();
  const [pickUpDate, setPickUpDate] = useState(todayISO());
  const [cost, setCost] = useState('');
  const [closetId, setClosetId] = useState('');

  useEffect(() => {
    if (closets.length && !closetId) setClosetId(closets.find((c) => c.isMain)?.id ?? closets[0].id);
  }, [closets, closetId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Mark Returned"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy}
            onClick={() =>
              onConfirm({
                pickUpDate: pickUpDate || undefined,
                cost: cost ? Number(cost) : undefined,
                closetId: closetId || undefined,
              })
            }
          >
            Mark Returned
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Return to Closet">
          <ClosetSelect value={closetId} onChange={setClosetId} closets={closets} />
        </Field>
        <Field label="Pickup Date">
          <input type="date" className={inputClass} value={pickUpDate} onChange={(e) => setPickUpDate(e.target.value)} />
        </Field>
        <Field label="Final Cost">
          <input type="number" className={inputClass} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mark Delivered / Picked Up / Fitting Complete
// ---------------------------------------------------------------------------

export function MarkDeliveredModal({
  isOpen,
  onClose,
  itemName,
  onConfirm,
  busy,
  title = 'Mark as Delivered',
}: BaseModalProps & { title?: string }) {
  const closets = useClosets();
  const [deliveredDate, setDeliveredDate] = useState(todayISO());
  const [closetId, setClosetId] = useState('');

  useEffect(() => {
    if (closets.length && !closetId) setClosetId(closets.find((c) => c.isMain)?.id ?? closets[0].id);
  }, [closets, closetId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy || !closetId}
            onClick={() => onConfirm({ closetId, deliveredDate: deliveredDate || undefined })}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Closet Location">
          <ClosetSelect value={closetId} onChange={setClosetId} closets={closets} />
        </Field>
        <Field label="Date">
          <input type="date" className={inputClass} value={deliveredDate} onChange={(e) => setDeliveredDate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Relocate
// ---------------------------------------------------------------------------

export function RelocateModal({
  isOpen,
  onClose,
  itemName,
  onConfirm,
  busy,
  currentClosetId,
}: BaseModalProps & { currentClosetId: string | null }) {
  const closets = useClosets();
  const [closetId, setClosetId] = useState(currentClosetId ?? '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Relocate Item"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth disabled={busy || !closetId} onClick={() => onConfirm({ closetId })}>
            Move
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="New Closet">
          <ClosetSelect value={closetId} onChange={setClosetId} closets={closets} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mark Purchased (wishlist -> owned or pending)
// ---------------------------------------------------------------------------

export function MarkPurchasedModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const closets = useClosets();
  const [arriving, setArriving] = useState(false);
  const [closetId, setClosetId] = useState('');
  const [cost, setCost] = useState('');
  const [storeName, setStoreName] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState(todayISO());
  // arriving fields
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');

  useEffect(() => {
    if (closets.length && !closetId) setClosetId(closets.find((c) => c.isMain)?.id ?? closets[0].id);
  }, [closets, closetId]);

  const submit = () => {
    if (arriving) {
      onConfirm({
        arriving: true,
        pendingType: 'being-delivered',
        carrier: carrier || undefined,
        trackingNumber: trackingNumber || undefined,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
      });
    } else {
      onConfirm({
        arriving: false,
        closetId: closetId || undefined,
        cost: cost ? Number(cost) : undefined,
        storeName: storeName || undefined,
        acquisitionDate: acquisitionDate || undefined,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Mark as Purchased"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth disabled={busy} onClick={submit}>
            Confirm
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setArriving(false)}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${
              !arriving ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
            }`}
          >
            Have it now
          </button>
          <button
            type="button"
            onClick={() => setArriving(true)}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${
              arriving ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
            }`}
          >
            Being delivered
          </button>
        </div>
        {arriving ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Carrier">
                <input className={inputClass} value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="FedEx" />
              </Field>
              <Field label="Tracking #">
                <input className={inputClass} value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
              </Field>
            </div>
            <Field label="Expected Delivery">
              <input
                type="date"
                className={inputClass}
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Closet">
              <ClosetSelect value={closetId} onChange={setClosetId} closets={closets} />
            </Field>
            <Field label="Price Paid">
              <input type="number" className={inputClass} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
            </Field>
            <Field label="Store">
              <input className={inputClass} value={storeName} onChange={(e) => setStoreName(e.target.value)} />
            </Field>
            <Field label="Purchase Date">
              <input
                type="date"
                className={inputClass}
                value={acquisitionDate}
                onChange={(e) => setAcquisitionDate(e.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// List for Sale
// ---------------------------------------------------------------------------

export function ListForSaleModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const [channel, setChannel] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [listingUrl, setListingUrl] = useState('');
  const [listedDate, setListedDate] = useState(todayISO());
  const [notes, setNotes] = useState('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="List for Sale"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy}
            onClick={() =>
              onConfirm({
                channel: channel || undefined,
                listPrice: listPrice ? Number(listPrice) : undefined,
                listingUrl: listingUrl || undefined,
                listedDate: listedDate || undefined,
                notes: notes || undefined,
              })
            }
          >
            List
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Channel">
          <input className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="e.g., Grailed" />
        </Field>
        <Field label="List Price">
          <input type="number" className={inputClass} value={listPrice} onChange={(e) => setListPrice(e.target.value)} />
        </Field>
        <Field label="Listing URL">
          <input className={inputClass} value={listingUrl} onChange={(e) => setListingUrl(e.target.value)} />
        </Field>
        <Field label="Listed Date">
          <input type="date" className={inputClass} value={listedDate} onChange={(e) => setListedDate(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mark Sold
// ---------------------------------------------------------------------------

export function MarkSoldModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const [salePrice, setSalePrice] = useState('');
  const [fees, setFees] = useState('');
  const [channel, setChannel] = useState('');
  const [soldDate, setSoldDate] = useState(todayISO());

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Mark as Sold"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy}
            onClick={() =>
              onConfirm({
                salePrice: salePrice ? Number(salePrice) : undefined,
                fees: fees ? Number(fees) : undefined,
                channel: channel || undefined,
                soldDate: soldDate || undefined,
              })
            }
          >
            Mark Sold
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Sale Price">
          <input type="number" className={inputClass} value={salePrice} onChange={(e) => setSalePrice(e.target.value)} />
        </Field>
        <Field label="Fees">
          <input type="number" className={inputClass} value={fees} onChange={(e) => setFees(e.target.value)} />
        </Field>
        <Field label="Channel">
          <input className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value)} />
        </Field>
        <Field label="Sold Date">
          <input type="date" className={inputClass} value={soldDate} onChange={(e) => setSoldDate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Log Wear
// ---------------------------------------------------------------------------

export function LogWearModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const [wornDate, setWornDate] = useState(todayISO());
  const [occasion, setOccasion] = useState('');
  const [locationText, setLocationText] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log a Wear"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy}
            onClick={() =>
              onConfirm({
                wornDate: wornDate || undefined,
                occasion: occasion || undefined,
                locationText: locationText || undefined,
                notes: notes || undefined,
              })
            }
          >
            Log Wear
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Date Worn">
          <input type="date" className={inputClass} value={wornDate} onChange={(e) => setWornDate(e.target.value)} />
        </Field>
        <Field label="Occasion">
          <select className={inputClass} value={occasion} onChange={(e) => setOccasion(e.target.value)}>
            <option value="">—</option>
            {OCCASIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Location">
          <input className={inputClass} value={locationText} onChange={(e) => setLocationText(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Log Cleaning
// ---------------------------------------------------------------------------

export function LogCleaningModal({ isOpen, onClose, itemName, onConfirm, busy }: BaseModalProps) {
  const [cleanedDate, setCleanedDate] = useState(todayISO());
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Cleaning"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={busy}
            onClick={() =>
              onConfirm({
                cleanedDate: cleanedDate || undefined,
                method: method || undefined,
                notes: notes || undefined,
              })
            }
          >
            Log Cleaning
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground line-clamp-1">{itemName}</p>
        <Field label="Date Cleaned">
          <input type="date" className={inputClass} value={cleanedDate} onChange={(e) => setCleanedDate(e.target.value)} />
        </Field>
        <Field label="Method">
          <select className={inputClass} value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">—</option>
            {CLEANING_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes">
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
